import React, { memo, useState } from 'react';

import { useSelector } from 'react-redux';

import PropTypes from 'prop-types';

import clsx from 'clsx';

import { useBrand } from '@atc/brand';
import { useFeatures } from '@atc/features';

import { Col, Row } from 'reaxl';
import { sendClick } from 'reaxl-analytics';

import { cpoFilterTileClick } from '@/analytics/srpAnalyticsHandlers';

import cpoContentDuck from '@/ducks/cpoContentDuck.js';

import certifiedSponsorFetcher from '@/fetchers/srp/certifiedSponsorFetcher';

import CertifiedPreOwnedModal from '@/components/CertifiedPreOwnedModal/CertifiedPreOwnedModal';
import CertifiedTile from '@/components/CertifiedTile';

const generateCertifiedTileMap = ({
    certifiedContentSimple,
    makeCodes,
    GM_MAKES,
    STELLANTIS_MAKES,
    BRANDED_MAKES,
}) => {
    // Makes that only show up once
    const hasSingleMake = (make, tileMap) => BRANDED_MAKES?.includes(make) && tileMap[`${make}_CERT`];
    const tileMap = {};

    for (const key of Object.keys(certifiedContentSimple)) {
        for (const make of makeCodes) {
            if (Object.keys(tileMap).length === 2) {
                break;
            }

            if (make === certifiedContentSimple?.[key]?.makeCode && !hasSingleMake(make, tileMap)) {
                tileMap[key] = {
                    ...certifiedContentSimple[key],
                    productId: key,
                };
            } else if (GM_MAKES?.[make]) {
                tileMap.CARBRAVO_CERT = {
                    ...certifiedContentSimple.CARBRAVO_CERT,
                    makeCode: make,
                    makeLabel: GM_MAKES[make],
                    productId: 'CARBRAVO_CERT',
                };
            } else if (STELLANTIS_MAKES?.[make]) {
                tileMap.STELLANTIS_OFF_CERT = {
                    ...certifiedContentSimple.STELLANTIS_OFF_CERT,
                    makeCode: make,
                    makeLabel: STELLANTIS_MAKES[make],
                    productId: 'STELLANTIS_OFF_CERT',
                };
            }
        }
    }

    return tileMap;
};
/**
 *
 *Certified Pre-Owned Details.
 * Display the data provided by WordPress, currently we are passing data from Json.
 */

function CertifiedTileFilterContainer({
    filtersValues = {},
    makeCodeKey = '',
    uiContext,
}) {
    const [showModal, setShowModal] = useState(false);
    const [modalData, setModalData] = useState({});
    const { isBrand } = useBrand();

    const {
        brand: [, { srp_pg_used: srpPageUsed }],
        cpo_programs: [, { gm_makes: GM_MAKES, stellantis_makes: STELLANTIS_MAKES, branded_makes: BRANDED_MAKES }],
        limit_cpo_programs: [enableLimitedCpoPrograms, { programs: LIMITED_CPO_PROGRAMS }],
        oem_cpo_srp: [, { condition_filter: isPanelCertTile }],
        remove_certified_tile: [removeCertifiedTile],
    } = useFeatures([
        'brand',
        'cpo_programs',
        'limit_cpo_programs',
        'oem_cpo_srp',
        'remove_certified_tile',
    ]);

    const certifiedContentSimple = useSelector(cpoContentDuck.selectors.selectCertifiedContentSimple);
    const makeCodeList = filtersValues?.[makeCodeKey];
    const makeCodes = makeCodeList && (typeof makeCodeList === 'object' ? makeCodeList : [makeCodeList]);
    const cpoHeading = isPanelCertTile ? 'Certified Program' : 'Certified Program Details';
    const isKbb = isBrand('kbb');

    const handleShowModal = async ({ event, productId, makeCode, makeLabel }) => {

        let cdl;

        if (productId === 'CARBRAVO_CERT') {
            cdl = {
                event: {
                    action: 'Click',
                    element: isKbb ? 'Learn More Filter Link' : 'Learn More Link',
                    tagIndex: 'srp-0166',
                },
            };
        }

        sendClick(cpoFilterTileClick, event, {
            cmp: `fts_sp_srch_type - ${srpPageUsed}`,
            makeCode,
            cdl,
        });

        try {
            const certifiedData = await certifiedSponsorFetcher({ productId });
            // Not display inspectionLink on SRP tile
            delete certifiedData.inspectionLink;
            setModalData({ certifiedData, makeLabel, makeCode });
            setShowModal(true);
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Error fetching certifiedSponsor:', error);
        }
    };

    let certifiedTileMap = {};
    if (makeCodes && certifiedContentSimple) {
        const allowedMakes = enableLimitedCpoPrograms
            ? makeCodes?.filter((make) => Object.keys(LIMITED_CPO_PROGRAMS).includes(make))
            : makeCodes;

        certifiedTileMap = generateCertifiedTileMap({
            certifiedContentSimple,
            makeCodes: allowedMakes,
            GM_MAKES,
            STELLANTIS_MAKES,
            BRANDED_MAKES,
        });
    }

    const colProps = isPanelCertTile ? {} : { xs: 5, sm: 3, md: 12, lg: 6 };

    if (removeCertifiedTile || Object.keys(certifiedTileMap).length === 0) {
        return null;
    }

    if (uiContext === 'link') {
        return (
            <>
                {Object.values(certifiedTileMap).map((content) => {
                    content.linkText = content.linkText || 'Learn more';
                    return (
                        <CertifiedTile
                            key={content.productId}
                            type="link"
                            data={content}
                            handleShowModal={handleShowModal}
                        />
                    );
                })}
                {showModal && (
                    <CertifiedPreOwnedModal
                        showModal={showModal}
                        handleCloseModal={() => setShowModal(false)}
                        data={modalData}
                        filtersValues={filtersValues}
                    />
                )}
            </>
        );
    }

    return (
        <>
            <div className="margin-top-5 text-bold text-gray text-size-300 text-size-sm-300">{cpoHeading}</div>
            <Row className="margin-top-3">
                {Object.values(certifiedTileMap).map((content) => (
                    <Col
                        key={`FilterTile-${content.productId}`}
                        {...colProps}
                    >
                        <CertifiedTile
                            data={content}
                            isPanelCertTile={isPanelCertTile}
                            handleShowModal={handleShowModal}
                        />
                    </Col>
                ))}
            </Row>

            {
                Object.values(certifiedTileMap).map((content) => content.linkText && (
                    <Row
                        key={`CertifiedLink-${content.productId}`}
                        className={clsx('padding-left-2', {
                            'margin-top-4': !isPanelCertTile,
                        })}
                    >
                        <CertifiedTile
                            type="link"
                            data={content}
                            handleShowModal={handleShowModal}
                        />
                    </Row>
                ))
            }
            {showModal && (
                <CertifiedPreOwnedModal
                    showModal={showModal}
                    handleCloseModal={() => setShowModal(false)}
                    data={modalData}
                    filtersValues={filtersValues}
                />
            )}
        </>
    );
}

CertifiedTileFilterContainer.propTypes = {
    filtersValues: PropTypes.shape({}),
    makeCodeKey: PropTypes.string.isRequired,
    uiContext: PropTypes.string,
};

CertifiedTileFilterContainer.defaultProps = {
    filtersValues: null,
    uiContext: undefined,
};

export default memo(CertifiedTileFilterContainer);
